/**
 * Configuración y lógica pura de la tabla de tickets: columnas disponibles,
 * criterios de búsqueda y preferencias de columnas (visibilidad y orden).
 *
 * No hace I/O ni depende de React: la tabla, sus hooks y los tests consumen
 * estas funciones. Para agregar una columna basta con declararla en
 * `TICKET_TABLE_COLUMNS` y registrar cómo se pinta en `TableTickets`.
 */

import { employeeName } from './employee'
import { getSlaInfo } from './sla'
import { getTicketType } from './ticketType'
import { nextSort, sortBy } from './sort'

/**
 * Columnas disponibles de la tabla, en su orden por defecto.
 * - `defaultVisible`: si aparece la primera vez que el usuario ve la tabla.
 * - `required`: no se puede ocultar (siempre visible).
 * - `className`: clases del `<th>`/`<td>` (p. ej. ocultar en móvil).
 * @type {Array<{key: string, label: string, defaultVisible: boolean,
 *   required?: boolean, className?: string}>}
 */
export const TICKET_TABLE_COLUMNS = [
  { key: 'id', label: '# ID', defaultVisible: true, required: true },
  { key: 'title', label: 'Título', defaultVisible: true, required: true },
  {
    key: 'type',
    label: 'Tipo',
    defaultVisible: true,
    className: 'd-none d-sm-table-cell',
  },
  {
    key: 'origin',
    label: 'Origen',
    defaultVisible: true,
    className: 'd-none d-md-table-cell',
  },
  { key: 'priority', label: 'Prioridad', defaultVisible: true },
  { key: 'status', label: 'Estado', defaultVisible: true },
  {
    key: 'queue',
    label: 'Cola',
    defaultVisible: true,
    className: 'd-none d-md-table-cell',
  },
  {
    key: 'responsible',
    label: 'Responsable',
    defaultVisible: false,
    className: 'd-none d-md-table-cell',
  },
  {
    key: 'author',
    label: 'Autor',
    defaultVisible: false,
    className: 'd-none d-md-table-cell',
  },
  { key: 'sla', label: 'SLA', defaultVisible: false },
  {
    key: 'createdAt',
    label: 'Creado',
    defaultVisible: false,
    className: 'd-none d-lg-table-cell',
  },
  {
    key: 'dueAt',
    label: 'Vence',
    defaultVisible: false,
    className: 'd-none d-lg-table-cell',
  },
]

/** Valores iniciales de los criterios de búsqueda (vacío = sin filtrar). */
export const DEFAULT_TICKET_FILTERS = {
  search: '',
  statusId: '',
  queueId: '',
  origin: '',
  type: '',
  priorityId: '',
  responsibleId: '',
  authorId: '',
  sla: '',
  createdFrom: '',
  createdTo: '',
}

/** Valor especial del filtro de responsable para "sin asignar". */
export const UNASSIGNED = '__none__'

/** Valor especial del filtro de cola para "sin cola". */
export const NO_QUEUE = '__none__'

/** Opciones del filtro de SLA (agrupan los estados de `getSlaInfo`). */
export const SLA_FILTER_OPTIONS = [
  { value: 'breached', label: 'Incumplido' },
  { value: 'at_risk', label: 'En riesgo' },
  { value: 'on_time', label: 'En tiempo / cumplido' },
  { value: 'none', label: 'Sin SLA' },
]

/** Estados de `getSlaInfo().overall` que abarca cada opción del filtro. */
const SLA_GROUPS = {
  breached: ['breached'],
  at_risk: ['at_risk'],
  on_time: ['on_track', 'met', 'pending'],
  none: ['none'],
}

/**
 * Convierte `YYYY-MM-DD` (input date) al inicio o fin de ese día en hora local.
 * @param {string} value
 * @param {boolean} endOfDay
 * @returns {number|null} Marca de tiempo o `null` si no hay valor.
 */
const dayBoundary = (value, endOfDay) => {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  return endOfDay
    ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
    : new Date(y, m - 1, d).getTime()
}

/** Quita etiquetas HTML sin depender del DOM (la descripción es texto rico). */
const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ')

/** Normaliza texto para búsquedas sin distinguir mayúsculas ni tildes. */
const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

/**
 * Filtra tickets según los criterios de búsqueda.
 *
 * La búsqueda de texto cubre ID, título, descripción y nombres del responsable
 * y del autor, sin distinguir mayúsculas ni tildes. El resto de criterios se
 * combinan con AND.
 *
 * @param {Array<Object>} items - Tickets a filtrar.
 * @param {Partial<typeof DEFAULT_TICKET_FILTERS>} filters - Criterios activos.
 * @param {Object} [context]
 * @param {Record<string, Object>} [context.employeeById] - Empleados por id
 *   (para el responsable, `responsibleId`).
 * @param {Record<string, Object>} [context.employeeByAuthId] - Empleados por
 *   auth id (para el autor, `userId`).
 * @param {number} [context.now=Date.now()] - Referencia para el SLA.
 * @returns {Array<Object>}
 */
export const filterTickets = (
  items = [],
  filters = {},
  { employeeById = {}, employeeByAuthId = {}, now = Date.now() } = {}
) => {
  const f = { ...DEFAULT_TICKET_FILTERS, ...filters }
  const term = normalize(f.search.trim())
  const from = dayBoundary(f.createdFrom, false)
  const to = dayBoundary(f.createdTo, true)
  const slaStates = f.sla ? SLA_GROUPS[f.sla] || [] : null

  return items.filter((t) => {
    if (f.statusId && String(t.statusId) !== String(f.statusId)) return false
    if (f.queueId === NO_QUEUE) {
      if (t.queueId) return false
    } else if (f.queueId && String(t.queueId) !== String(f.queueId)) {
      return false
    }
    if (f.origin && t.systemOrigin !== f.origin) return false
    if (f.type && t.type !== f.type) return false
    if (f.priorityId && String(t.priorityId) !== String(f.priorityId))
      return false

    if (f.responsibleId === UNASSIGNED) {
      if (t.responsibleId) return false
    } else if (
      f.responsibleId &&
      String(t.responsibleId) !== String(f.responsibleId)
    ) {
      return false
    }

    if (f.authorId && String(t.userId) !== String(f.authorId)) return false

    if (from !== null || to !== null) {
      const created = t.createdAt ? new Date(t.createdAt).getTime() : NaN
      if (Number.isNaN(created)) return false
      if (from !== null && created < from) return false
      if (to !== null && created > to) return false
    }

    if (slaStates && !slaStates.includes(getSlaInfo(t, now).overall))
      return false

    if (term) {
      const haystack = normalize(
        [
          t.id,
          t.title,
          stripHtml(t.description),
          employeeName(employeeById[t.responsibleId]),
          employeeName(employeeByAuthId[t.userId]),
        ].join(' ')
      )
      if (!haystack.includes(term)) return false
    }

    return true
  })
}

/**
 * Cantidad de criterios activos (para el indicador del botón de filtros).
 * @param {Object} filters
 * @returns {number}
 */
export const countActiveFilters = (filters = {}) =>
  Object.keys(DEFAULT_TICKET_FILTERS).filter(
    (key) => filters[key] && filters[key] !== DEFAULT_TICKET_FILTERS[key]
  ).length

/**
 * Preferencia de columnas por defecto: todas, en su orden, con su visibilidad.
 * @param {Array<Object>} columns - Definición de columnas.
 * @returns {Array<{key: string, visible: boolean}>}
 */
export const defaultColumnLayout = (columns) =>
  columns.map((col) => ({ key: col.key, visible: col.defaultVisible }))

/**
 * Reconcilia la preferencia guardada con las columnas actuales: descarta claves
 * que ya no existen, agrega al final las columnas nuevas y fuerza visibles las
 * obligatorias. Así una preferencia antigua nunca rompe la tabla.
 *
 * @param {Array<{key: string, visible: boolean}>|unknown} saved
 * @param {Array<Object>} columns
 * @returns {Array<{key: string, visible: boolean}>}
 */
export const normalizeColumnLayout = (saved, columns) => {
  if (!Array.isArray(saved)) return defaultColumnLayout(columns)

  const byKey = new Map(columns.map((col) => [col.key, col]))
  const seen = new Set()
  const layout = []

  for (const entry of saved) {
    const col = byKey.get(entry?.key)
    if (!col || seen.has(col.key)) continue
    seen.add(col.key)
    layout.push({ key: col.key, visible: col.required || !!entry.visible })
  }

  for (const col of columns) {
    if (!seen.has(col.key))
      layout.push({ key: col.key, visible: col.defaultVisible })
  }

  return layout
}

/**
 * Mueve una columna de la posición `from` a `to` (devuelve un arreglo nuevo).
 * @param {Array} layout
 * @param {number} from
 * @param {number} to
 * @returns {Array}
 */
export const moveItem = (layout, from, to) => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= layout.length ||
    to >= layout.length
  )
    return layout
  const next = [...layout]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Orden por defecto de la tabla: los más recientes primero. */
export const DEFAULT_TICKET_SORT = { key: 'createdAt', dir: 'desc' }

/** Gravedad de cada estado de SLA para ordenar (mayor = más urgente). */
const SLA_RANK = {
  breached: 5,
  at_risk: 4,
  pending: 3,
  on_track: 2,
  met: 1,
  none: 0,
}

/** Marca de tiempo de una fecha ISO, o `null` si no hay. */
const toTime = (value) => (value ? new Date(value).getTime() : null)

/**
 * Valor por el que se ordena cada columna de `TICKET_TABLE_COLUMNS`.
 * Devuelve número o texto; `null`/'' se consideran vacíos.
 * @type {Record<string, (t: Object, ctx: Object) => (number|string|null)>}
 */
const SORT_ACCESSORS = {
  id: (t) => Number(t.id),
  title: (t) => t.title || '',
  type: (t) => getTicketType(t.type).label,
  origin: (t) => t.systemOrigin || '',
  priority: (t) => Number(t.priorityId) || null,
  // Los ids de estado siguen el flujo (Nuevo → … → Finalizado).
  status: (t) => Number(t.statusId) || null,
  queue: (t, { queueById = {} }) => queueById[t.queueId]?.name || '',
  responsible: (t, { employeeById = {} }) =>
    employeeName(employeeById[t.responsibleId]),
  author: (t, { employeeByAuthId = {} }) =>
    employeeName(employeeByAuthId[t.userId]),
  sla: (t, { now }) => SLA_RANK[getSlaInfo(t, now).overall],
  createdAt: (t) => toTime(t.createdAt),
  dueAt: (t) => toTime(t.dueAt),
}

/** Indica si una columna se puede ordenar. */
export const isSortable = (key) => key in SORT_ACCESSORS

/**
 * Ordena tickets por una columna. Los valores vacíos van siempre al final,
 * sea cual sea la dirección; el texto se compara en español y sin distinguir
 * mayúsculas ni tildes.
 *
 * @param {Array<Object>} items
 * @param {{key: string, dir: 'asc'|'desc'}} sort
 * @param {Object} [context] - `employeeById`, `employeeByAuthId`, `now`.
 * @returns {Array<Object>} Copia ordenada (no muta `items`).
 */
export const sortTickets = (
  items = [],
  sort = DEFAULT_TICKET_SORT,
  context = {}
) => {
  const accessor = SORT_ACCESSORS[sort?.key]
  if (!accessor) return items
  const ctx = { now: Date.now(), ...context }
  return sortBy(items, (t) => accessor(t, ctx), sort.dir)
}

// Se reexporta para quienes ya lo importaban desde aquí.
export { nextSort }
